import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithOrg } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import ExportPreviewRenderer from './ExportPreviewRenderer';
import AIOutputNotice from './AIOutputNotice';

import {
  ArrowSquareOut,
  CaretLeft,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Cloud,
  DownloadSimple,
  Envelope,
  Eye,
  File,
  FileText,
  FolderOpen,
  Link as LinkIcon,
  PaperPlaneRight,
  WarningCircle,
  X
} from '@phosphor-icons/react';
/**
 * UniversalExportModal - Platform-wide export for all agents
 * 
 * Features:
 * - Left panel: Schema-driven preview (or generic fallback)
 * - Right panel: Export destination options
 * 
 * Export Destinations:
 * 1. DownloadSimple DOCX (always available)
 * 2. DownloadSimple PDF (always available)
 * 3. Microsoft Word (OneDrive) - requires M365 integration
 * 4. Outlook Email - requires M365 integration
 * 5. Google Docs - requires Google Workspace
 * 6. Gmail - requires Google Workspace
 */

// Microsoft 365 icon
const Microsoft365Icon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zm1-18H21v8.5h-8.5V3zm8.5 9.5V21h-8.5v-8.5H21z"/>
  </svg>
);

// Google icon
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Destination definitions
const DESTINATIONS = {
  download_docx: {
    id: 'download_docx',
    label: 'Download DOCX',
    description: 'Microsoft Word format',
    icon: FileText,
    iconColor: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    requiresIntegration: null,
    category: 'download'
  },
  download_pdf: {
    id: 'download_pdf',
    label: 'Download PDF',
    description: 'Portable Document Format',
    icon: FileText,
    iconColor: 'text-red-400',
    bgColor: 'bg-red-500/20',
    requiresIntegration: null,
    category: 'download'
  },
  download_xlsx: {
    id: 'download_xlsx',
    label: 'Download Excel',
    description: 'Excel spreadsheet format',
    icon: FileText,
    iconColor: 'text-green-400',
    bgColor: 'bg-green-500/20',
    requiresIntegration: null,
    category: 'download',
    uosOnly: true  // Only show for UOS sessions
  },
  download_markdown: {
    id: 'download_markdown',
    label: 'Download Markdown',
    description: 'Plain text markdown',
    icon: FileText,
    iconColor: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    requiresIntegration: null,
    category: 'download',
    uosOnly: true  // Only show for UOS sessions
  },
  microsoft_word: {
    id: 'microsoft_word',
    label: 'Save to OneDrive',
    description: 'Microsoft Word on OneDrive',
    icon: Microsoft365Icon,
    iconColor: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    requiresIntegration: 'microsoft365',
    category: 'cloud'
  },
  outlook_email: {
    id: 'outlook_email',
    label: 'Send via Outlook',
    description: 'Email with attachment',
    icon: Envelope,
    iconColor: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    requiresIntegration: 'microsoft365',
    category: 'email'
  },
  google_docs: {
    id: 'google_docs',
    label: 'Save to Google Drive',
    description: 'Google Docs format',
    icon: GoogleIcon,
    iconColor: 'text-green-400',
    bgColor: 'bg-green-500/20',
    requiresIntegration: 'google_workspace',
    category: 'cloud'
  },
  gmail: {
    id: 'gmail',
    label: 'Send via Gmail',
    description: 'Email with attachment',
    icon: Envelope,
    iconColor: 'text-red-400',
    bgColor: 'bg-red-500/20',
    requiresIntegration: 'google_workspace',
    category: 'email'
  }
};

const UniversalExportModal = ({ 
  isOpen, 
  onClose, 
  agentId,
  agentName,
  sessionId,
  contextTitle,  // Optional: custom title for the export
  sourceFilename,  // Optional: original input filename
  // Preview data (passed from parent if available)
  outputData,      // session output data
  exportPreviewSchema,  // schema from agent template
  exportPreviewEnabled,  // whether preview is enabled for this agent (hero agents)
  uosOutput       // UOS v2.0 formatted output (for new export engine)
}) => {
  const { activeOrganization } = useOrganization();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // State
  const [integrationStatus, setIntegrationStatus] = useState({
    user_email: '',
    microsoft365: { connected: false },
    google_workspace: { connected: false }
  });
  const [loading, setLoading] = useState(true);
  const [selectedDestination, setSelectedDestination] = useState('download_docx');
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(null);
  // Preview collapsed by default for non-hero agents (those without export_preview_enabled)
  const [showPreview, setShowPreview] = useState(exportPreviewEnabled === true);
  
  // Preview data state (fetched if not provided)
  const [previewData, setPreviewData] = useState(null);
  const [previewSchema, setPreviewSchema] = useState(exportPreviewSchema || null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Email fields
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [includeDocx, setIncludeDocx] = useState(true);
  const [includePdf, setIncludePdf] = useState(false);
  
  // OAuth popup state
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [oauthPopup, setOauthPopup] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Fetch preview data and integration status on mount
  useEffect(() => {
    if (isOpen && activeOrganization) {
      fetchIntegrationStatus();
      
      // Fetch preview data if not provided
      if (!outputData && sessionId) {
        fetchPreviewData();
      } else if (outputData) {
        setPreviewData(outputData);
      }
    }
  }, [isOpen, activeOrganization, sessionId]);
  
  // Listen for OAuth completion messages from popup
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      // Validate message type
      if (event.data?.type !== 'OAUTH_COMPLETE') return;
      
      const { provider, success, error, email } = event.data;
      
      // Clear connecting state
      setConnectingProvider(null);
      
      // Clear polling if active
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
      // Close popup reference
      if (oauthPopup && !oauthPopup.closed) {
        oauthPopup.close();
      }
      setOauthPopup(null);
      
      if (success) {
        toast({
          title: 'Connected!',
          description: email ? `Connected as ${email}` : `${provider} connected successfully`,
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        
        // Refresh integration status
        fetchIntegrationStatus();
      } else {
        toast({
          title: 'Connection Failed',
          description: error || 'Failed to connect. Please try again.',
          variant: 'destructive'
        });
      }
    };
    
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [oauthPopup, pollingInterval, toast]);
  
  // Cleanup on unmount or modal close
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (oauthPopup && !oauthPopup.closed) {
        oauthPopup.close();
      }
    };
  }, [pollingInterval, oauthPopup]);
  
  
  // Fetch preview data from session
  const fetchPreviewData = async () => {
    if (!sessionId || !activeOrganization) return;
    
    setPreviewLoading(true);
    try {
      // Fetch session output
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/sessions/${sessionId}/output`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        
        // Try to get schema from agent template if not provided
        if (!previewSchema && agentId) {
          const agentResponse = await fetchWithOrg(
            `${BACKEND_URL}/api/agents/${agentId}`,
            {},
            activeOrganization.organization_id
          );
          
          if (agentResponse.ok) {
            const agentData = await agentResponse.json();
            if (agentData.export_preview_schema) {
              setPreviewSchema(agentData.export_preview_schema);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching preview data:', err);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Set default email recipient when integration status loads
  useEffect(() => {
    if (integrationStatus.user_email && !emailTo) {
      setEmailTo(integrationStatus.user_email);
    }
  }, [integrationStatus.user_email]);
  
  // Generate default subject
  useEffect(() => {
    if (!emailSubject && agentName) {
      const date = new Date().toLocaleDateString();
      const context = contextTitle || sourceFilename || 'Output';
      setEmailSubject(`${agentName} Output – ${context} – ${date}`);
    }
  }, [agentName, contextTitle, sourceFilename]);
  
  const fetchIntegrationStatus = async () => {
    setLoading(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exports/integrations/status`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setIntegrationStatus(data);
        // Set default email to user's email
        if (data.user_email) {
          setEmailTo(data.user_email);
        }
      }
    } catch (err) {
      console.error('Error fetching integration status:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Check if a destination is available based on UOS support
  const hasUosOutput = uosOutput?.version === '2.0' || previewData?.uos_output?.version === '2.0';
  
  const isDestinationAvailable = (destId) => {
    const dest = DESTINATIONS[destId];
    // UOS-only formats require UOS output
    if (dest.uosOnly && !hasUosOutput) return false;
    if (!dest.requiresIntegration) return true;
    return integrationStatus[dest.requiresIntegration]?.connected;
  };
  
  const handleExport = async () => {
    setExporting(true);
    setExportSuccess(null);
    
    try {
      const dest = DESTINATIONS[selectedDestination];
      
      if (dest.category === 'download') {
        // Determine format from destination ID
        let format = 'docx';
        if (selectedDestination === 'download_pdf') format = 'pdf';
        else if (selectedDestination === 'download_xlsx') format = 'xlsx';
        else if (selectedDestination === 'download_markdown') format = 'markdown';
        
        let response;
        if (hasUosOutput && sessionId) {
          // Use UOF v2.0 Export Engine for sessions with UOS output
          response = await fetchWithOrg(
            `${BACKEND_URL}/api/exports/uos/${sessionId}/download`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                format: format,
                template: 'professional',
                include_branding: true
              })
            },
            activeOrganization.organization_id
          );
        } else {
          // Fallback to legacy export endpoint
          response = await fetchWithOrg(
            `${BACKEND_URL}/api/exports/generate`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                agent_id: agentId,
                session_id: sessionId,
                format: format,
                context_title: contextTitle
              })
            },
            activeOrganization.organization_id
          );
        }
        
        if (response.ok) {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = `export.${format}`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match) filename = match[1];
          }
          
          // DownloadSimple file
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          
          setExportSuccess({ type: 'download', filename });
          toast({
            title: 'Download Complete',
            description: `Downloaded ${filename}`,
            className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
          });
        } else {
          // Try to get error details from response
          let errorMessage = `Download failed (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // If response is not JSON, use status text
            errorMessage = `Download failed: ${response.statusText || response.status}`;
          }
          throw new Error(errorMessage);
        }
      } 
      else if (dest.category === 'cloud') {
        // Cloud export
        const provider = selectedDestination === 'microsoft_word' ? 'microsoft' : 'google';
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/exports/drive`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              provider: provider,
              agent_id: agentId,
              session_id: sessionId,
              format: 'docx',
              context_title: contextTitle
            })
          },
          activeOrganization.organization_id
        );
        
        const data = await response.json();
        
        if (data.success) {
          setExportSuccess({ type: 'cloud', url: data.url, path: data.folder_path, filename: data.filename });
          toast({
            title: 'Export Complete',
            description: `Saved to ${dest.label}`,
            className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
          });
          
          // Open file in new tab
          if (data.url) {
            window.open(data.url, '_blank');
          }
        } else {
          throw new Error(data.message || 'Export failed');
        }
      }
      else if (dest.category === 'email') {
        // Email export
        const provider = selectedDestination === 'outlook_email' ? 'outlook' : 'gmail';
        const recipients = emailTo.split(',').map(e => e.trim()).filter(Boolean);
        
        if (recipients.length === 0) {
          toast({
            title: 'Error',
            description: 'Please enter at least one recipient',
            variant: 'destructive'
          });
          setExporting(false);
          return;
        }
        
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/exports/email`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              provider: provider,
              agent_id: agentId,
              session_id: sessionId,
              to: recipients,
              subject: emailSubject,
              message: emailMessage,
              include_docx: includeDocx,
              include_pdf: includePdf,
              context_title: contextTitle
            })
          },
          activeOrganization.organization_id
        );
        
        const data = await response.json();
        
        if (data.success) {
          setExportSuccess({ type: 'email', recipients: data.recipients });
          toast({
            title: 'Email Sent',
            description: `Sent to ${recipients.join(', ')}`,
            className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
          });
        } else {
          throw new Error(data.message || 'Failed to send email');
        }
      }
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: 'Export Failed',
        description: err.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };
  
  const handleConnectIntegration = async (provider) => {
    // Don't start another connection if one is in progress
    if (connectingProvider) return;
    
    // Get org_id and user_id explicitly
    const orgId = activeOrganization?.organization_id;
    const userId = user?.id;
    
    // Validate we have required context
    if (!orgId) {
      toast({
        title: 'Missing Context',
        description: "Couldn't determine workspace/org. Please refresh the page.",
        variant: 'destructive'
      });
      return;
    }
    
    if (!userId) {
      toast({
        title: 'Not Authenticated',
        description: "Please log in again to connect integrations.",
        variant: 'destructive'
      });
      return;
    }
    
    setConnectingProvider(provider);
    
    try {
      // Build URL with explicit query params for reliability
      const url = new URL(`${BACKEND_URL}/api/exports/oauth/initiate/${provider}`);
      url.searchParams.set('org_id', orgId);
      url.searchParams.set('user_id', userId);
      
      // Fetch OAuth authorization URL from backend
      // PaperPlaneRight both query params AND headers for maximum compatibility
      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': orgId,
          'X-User-Id': userId
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Log detailed error for debugging
        console.error('[OAuth Initiate Error]', {
          status: response.status,
          error: errorData,
          provider,
          orgId,
          userId
        });
        throw new Error(errorData.detail || errorData.error || `Failed to initiate connection (${response.status})`);
      }
      
      const data = await response.json();
      const authUrl = data.authorization_url;
      
      if (!authUrl) {
        throw new Error('No authorization URL received from server');
      }
      
      console.log('[OAuth] Opening popup for:', provider, 'URL:', authUrl.substring(0, 100) + '...');
      
      // Calculate popup window position (centered)
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      // Open OAuth in popup window
      const popup = window.open(
        authUrl,
        `oauth_${provider}`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );
      
      if (!popup || popup.closed) {
        // Popup was blocked
        toast({
          title: 'Popup Blocked',
          description: 'Please allow popups for this site and try again.',
          variant: 'destructive'
        });
        setConnectingProvider(null);
        return;
      }
      
      setOauthPopup(popup);
      
      // Start polling as fallback (in case postMessage doesn't work)
      const pollId = setInterval(async () => {
        // Check if popup was closed
        if (popup.closed) {
          clearInterval(pollId);
          setPollingInterval(null);
          
          // Give a moment for postMessage to arrive, then check status
          setTimeout(async () => {
            if (connectingProvider) {
              // postMessage didn't arrive, check status manually
              await fetchIntegrationStatus();
              setConnectingProvider(null);
            }
          }, 500);
          return;
        }
        
        // Poll integration status every 2 seconds
        try {
          // Build URL with explicit query params
          const statusUrl = new URL(`${BACKEND_URL}/api/exports/integrations/status`);
          statusUrl.searchParams.set('org_id', orgId);
          statusUrl.searchParams.set('user_id', userId);
          
          const statusResponse = await fetch(statusUrl.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Organization-Id': orgId,
              'X-User-Id': userId
            }
          });
          
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            const isNowConnected = status[provider]?.connected;
            
            if (isNowConnected) {
              // Connected! Update status and close popup
              clearInterval(pollId);
              setPollingInterval(null);
              setIntegrationStatus(status);
              setConnectingProvider(null);
              
              if (!popup.closed) {
                popup.close();
              }
              setOauthPopup(null);
              
              toast({
                title: 'Connected!',
                description: `${provider === 'microsoft365' ? 'Microsoft 365' : 'Google Workspace'} connected successfully`,
                className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
              });
            }
          }
        } catch (e) {
          console.error('Error polling status:', e);
        }
      }, 2000);
      
      setPollingInterval(pollId);
      
      // Stop polling after 2 minutes (timeout)
      setTimeout(() => {
        if (pollId) {
          clearInterval(pollId);
          setPollingInterval(null);
          setConnectingProvider(null);
        }
      }, 120000);
      
    } catch (err) {
      console.error('Error initiating OAuth:', err);
      toast({
        title: 'Connection Failed',
        description: err.message || 'Failed to start connection. Please try again.',
        variant: 'destructive'
      });
      setConnectingProvider(null);
    }
  };
  
  const renderDestinationCard = (destId) => {
    const dest = DESTINATIONS[destId];
    const isAvailable = isDestinationAvailable(destId);
    const isSelected = selectedDestination === destId;
    const IconComponent = dest.icon;
    
    return (
      <div
        key={destId}
        onClick={() => isAvailable && setSelectedDestination(destId)}
        className={`relative p-4 rounded-xl border transition-all cursor-pointer ${
          isSelected 
            ? 'border-[#6366F1] bg-[#6366F1]/10' 
            : isAvailable
              ? 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.02)]'
              : 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.01)] opacity-60 cursor-not-allowed'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${dest.bgColor} flex items-center justify-center ${dest.iconColor}`}>
            {typeof IconComponent === 'function' ? <IconComponent /> : <IconComponent size={20} />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">{dest.label}</p>
            <p className="text-xs text-gray-500">{dest.description}</p>
          </div>
          {isSelected && (
            <CheckCircle size={20} className="text-[#6366F1]" />
          )}
          {!isAvailable && dest.requiresIntegration && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleConnectIntegration(dest.requiresIntegration);
              }}
              disabled={connectingProvider === dest.requiresIntegration}
              className="px-2 py-1 text-xs text-gray-400 border border-[rgba(255,255,255,0.1)] rounded hover:border-[rgba(255,255,255,0.2)] disabled:opacity-50 flex items-center gap-1"
            >
              {connectingProvider === dest.requiresIntegration ? (
                <>
                  <CircleNotch size={12} className="animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect'
              )}
            </button>
          )}
        </div>
      </div>
    );
  };
  
  if (!isOpen) return null;
  
  const selectedDest = DESTINATIONS[selectedDestination];
  const showEmailFields = selectedDest?.category === 'email';
  
  // Extract structured data for preview
  const getPreviewStructuredData = () => {
    if (!previewData) return null;
    
    // Try different possible locations for structured data
    const content = previewData.content || previewData;
    return content.structured_json || 
           content.output_structured_json || 
           content.structured ||
           content;
  };
  
  // Extract text output for preview
  const getPreviewTextOutput = () => {
    if (!previewData) return '';
    
    const content = previewData.content || previewData;
    return content.output_text || 
           content.text || 
           content.markdown ||
           content.summary ||
           '';
  };
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      
      {/* Modal - Wider for two-panel layout */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl z-50 px-4 max-h-[90vh]">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="relative px-6 py-4 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
            <button 
              onClick={onClose} 
              className="absolute top-4 right-5 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                  <DownloadSimple size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Export Results</h2>
                  <p className="text-sm text-gray-400">{agentName}</p>
                </div>
              </div>
              
              {/* Preview Toggle */}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-colors ${
                  showPreview 
                    ? 'bg-[#6366F1]/20 text-[#6366F1]' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Eye size={14} />
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            </div>
          </div>
          
          {/* Content - Two Panel Layout */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Panel - Preview */}
            {showPreview && (
              <div className="w-1/2 border-r border-[rgba(255,255,255,0.1)] p-5 overflow-y-auto">
                <div className="flex items-center gap-2 mb-4">
                  <Eye size={16} className="text-[#6366F1]" />
                  <h3 className="text-sm font-medium text-white">Preview</h3>
                </div>
                
                {previewLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <CircleNotch size={24} className="animate-spin text-[#6366F1]" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* AI Output Notice - EU AI Act Aligned Transparency */}
                    <AIOutputNotice variant="compact" className="mb-2" />
                    
                    <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                      <ExportPreviewRenderer
                        schema={previewSchema}
                        data={getPreviewStructuredData()}
                        outputText={getPreviewTextOutput()}
                        agentName={agentName}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Right Panel - Export Options */}
            <div className={`${showPreview ? 'w-1/2' : 'w-full'} p-5 overflow-y-auto`}>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <CircleNotch size={24} className="animate-spin text-[#6366F1]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* DownloadSimple Options */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-2">
                      DownloadSimple
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {renderDestinationCard('download_docx')}
                      {renderDestinationCard('download_pdf')}
                      {/* UOS-only formats - show when UOS output is available */}
                      {hasUosOutput && (
                        <>
                          {renderDestinationCard('download_xlsx')}
                          {renderDestinationCard('download_markdown')}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* 
                   * CLOUD SAVE OPTIONS - TEMPORARILY HIDDEN
                   * Re-implement when OAuth infrastructure is available on Emergent platform
                   * The OAuth providers and endpoints are ready in:
                   * - /app/backend/oauth_providers/google_workspace_provider.py
                   * - /app/backend/oauth_providers/microsoft365_provider.py
                   * - /app/backend/universal_export_routes.py (OAuth initiate/callback endpoints)
                   */}
                  
                  {/* Email Fields (shown when email destination selected) */}
                  {showEmailFields && isDestinationAvailable(selectedDestination) && (
                    <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          To <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          placeholder="email@example.com"
                          className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Message (optional)
                        </label>
                        <textarea
                          value={emailMessage}
                          onChange={(e) => setEmailMessage(e.target.value)}
                          rows={2}
                          placeholder="Add a personal note..."
                          className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#6366F1] focus:outline-none resize-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Attachments
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={includeDocx}
                              onChange={(e) => setIncludeDocx(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                            />
                            <span className="text-sm text-gray-300">DOCX</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={includePdf}
                              onChange={(e) => setIncludePdf(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                            />
                            <span className="text-sm text-gray-300">PDF</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Where it will save preview (for cloud) */}
                  {selectedDest?.category === 'cloud' && isDestinationAvailable(selectedDestination) && (
                    <div className="p-2 rounded-lg bg-[rgba(99,102,241,0.05)] border border-[rgba(99,102,241,0.15)]">
                      <div className="flex items-center gap-2 text-sm">
                        <FolderOpen size={14} className="text-[#6366F1]" />
                        <span className="text-gray-400">Saves to:</span>
                        <span className="text-white text-xs">Man & Machine / AI Reports / {new Date().getFullYear()}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Success State */}
                  {exportSuccess && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                      <div className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-green-400" />
                        <div>
                          {exportSuccess.type === 'download' && (
                            <p className="text-green-400 text-sm">Downloaded {exportSuccess.filename}</p>
                          )}
                          {exportSuccess.type === 'cloud' && (
                            <>
                              <p className="text-green-400 text-sm">Saved to {exportSuccess.path}</p>
                              {exportSuccess.url && (
                                <a 
                                  href={exportSuccess.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-[#6366F1] hover:underline flex items-center gap-1 mt-1"
                                >
                                  Open file <ArrowSquareOut size={10} />
                                </a>
                              )}
                            </>
                          )}
                          {exportSuccess.type === 'email' && (
                            <p className="text-green-400 text-sm">Email sent to {exportSuccess.recipients?.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-5 py-3 border-t border-[rgba(255,255,255,0.1)] flex justify-between gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] text-sm"
            >
              {exportSuccess ? 'Done' : 'Cancel'}
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !isDestinationAvailable(selectedDestination) || (showEmailFields && !emailTo.trim())}
              className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {exporting ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  {selectedDest?.category === 'email' ? <PaperPlaneRight size={16} /> : <DownloadSimple size={16} />}
                  {selectedDest?.category === 'email' ? 'Send' : 'Export'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversalExportModal;
